/**
 * Operations Zustand Store
 *
 * Manages all state for the Platform Operations section including:
 * - Platform health status monitoring
 * - Maintenance mode controls
 * - Queue management
 * - External provider status
 * - Cache invalidation
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  PlatformHealthStatus,
  PlatformHealthCheck,
  MaintenanceMode,
  ToggleMaintenanceModeDto,
  QueueStats,
  ProviderStatus,
  HealthStatus,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface OperationsError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface OperationsState {
  // Health
  healthStatus: PlatformHealthStatus | null;
  isLoadingHealth: boolean;
  healthHistory: PlatformHealthCheck[];
  isLoadingHealthHistory: boolean;
  healthError: OperationsError | null;

  // Maintenance
  maintenanceMode: MaintenanceMode | null;
  isLoadingMaintenance: boolean;
  isTogglingMaintenance: boolean;
  maintenanceError: OperationsError | null;

  // Queues
  queueStats: QueueStats[];
  isLoadingQueues: boolean;
  isPausingQueue: string | null; // queue name being paused
  isResumingQueue: string | null; // queue name being resumed
  queueError: OperationsError | null;

  // Providers
  providerStatus: ProviderStatus[];
  isLoadingProviders: boolean;
  providerError: OperationsError | null;

  // Cache
  isClearingCache: boolean;
  lastCacheClearAt: Date | null;
  cacheError: OperationsError | null;

  // Actions - Health
  loadHealthStatus: () => Promise<void>;
  loadHealthHistory: () => Promise<void>;

  // Actions - Maintenance
  loadMaintenanceMode: () => Promise<void>;
  toggleMaintenanceMode: (dto: ToggleMaintenanceModeDto) => Promise<boolean>;

  // Actions - Queues
  loadQueueStats: () => Promise<void>;
  pauseQueue: (name: string) => Promise<boolean>;
  resumeQueue: (name: string) => Promise<boolean>;

  // Actions - Providers
  loadProviderStatus: () => Promise<void>;

  // Actions - Cache
  invalidateCache: () => Promise<boolean>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Health
  healthStatus: null as PlatformHealthStatus | null,
  isLoadingHealth: false,
  healthHistory: [] as PlatformHealthCheck[],
  isLoadingHealthHistory: false,
  healthError: null as OperationsError | null,

  // Maintenance
  maintenanceMode: null as MaintenanceMode | null,
  isLoadingMaintenance: false,
  isTogglingMaintenance: false,
  maintenanceError: null as OperationsError | null,

  // Queues
  queueStats: [] as QueueStats[],
  isLoadingQueues: false,
  isPausingQueue: null as string | null,
  isResumingQueue: null as string | null,
  queueError: null as OperationsError | null,

  // Providers
  providerStatus: [] as ProviderStatus[],
  isLoadingProviders: false,
  providerError: null as OperationsError | null,

  // Cache
  isClearingCache: false,
  lastCacheClearAt: null as Date | null,
  cacheError: null as OperationsError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useOperationsStore = create<OperationsState>((set) => ({
  ...getInitialState(),

  // ============================================
  // Health Actions
  // ============================================

  loadHealthStatus: async () => {
    set({ isLoadingHealth: true, healthError: null });
    try {
      const api = getApiClient();
      const data = await api.get<PlatformHealthStatus>(API_ENDPOINTS.SUPER_ADMIN.OPERATIONS.HEALTH);

      set({
        healthStatus: data ?? null,
        isLoadingHealth: false,
      });
    } catch (error) {
      set({
        isLoadingHealth: false,
        healthError: {
          code: 'LOAD_HEALTH_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load health status',
        },
      });
    }
  },

  loadHealthHistory: async () => {
    set({ isLoadingHealthHistory: true });
    try {
      const api = getApiClient();
      const data = await api.get<PlatformHealthCheck[]>(API_ENDPOINTS.SUPER_ADMIN.OPERATIONS.HEALTH_HISTORY);

      set({
        healthHistory: Array.isArray(data) ? data : [],
        isLoadingHealthHistory: false,
      });
    } catch {
      set({ isLoadingHealthHistory: false });
    }
  },

  // ============================================
  // Maintenance Actions
  // ============================================

  loadMaintenanceMode: async () => {
    set({ isLoadingMaintenance: true, maintenanceError: null });
    try {
      const api = getApiClient();
      const data = await api.get<MaintenanceMode>(API_ENDPOINTS.SUPER_ADMIN.OPERATIONS.MAINTENANCE);

      set({
        maintenanceMode: data ?? null,
        isLoadingMaintenance: false,
      });
    } catch (error) {
      set({
        isLoadingMaintenance: false,
        maintenanceError: {
          code: 'LOAD_MAINTENANCE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load maintenance status',
        },
      });
    }
  },

  toggleMaintenanceMode: async (dto: ToggleMaintenanceModeDto): Promise<boolean> => {
    set({ isTogglingMaintenance: true, maintenanceError: null });
    try {
      const api = getApiClient();
      const data = await api.post<MaintenanceMode>(API_ENDPOINTS.SUPER_ADMIN.OPERATIONS.MAINTENANCE, dto);

      set({
        maintenanceMode: data ?? null,
        isTogglingMaintenance: false,
      });
      return true;
    } catch (error) {
      set({
        isTogglingMaintenance: false,
        maintenanceError: {
          code: 'TOGGLE_MAINTENANCE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to toggle maintenance mode',
        },
      });
      return false;
    }
  },

  // ============================================
  // Queue Actions
  // ============================================

  loadQueueStats: async () => {
    set({ isLoadingQueues: true, queueError: null });
    try {
      const api = getApiClient();
      const data = await api.get<QueueStats[]>(API_ENDPOINTS.SUPER_ADMIN.OPERATIONS.QUEUES_STATS);

      set({
        queueStats: Array.isArray(data) ? data : [],
        isLoadingQueues: false,
      });
    } catch (error) {
      set({
        isLoadingQueues: false,
        queueError: {
          code: 'LOAD_QUEUES_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load queue stats',
        },
      });
    }
  },

  pauseQueue: async (name: string): Promise<boolean> => {
    set({ isPausingQueue: name, queueError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.OPERATIONS.QUEUE_PAUSE(name), {});

      // Update local state optimistically
      set((state) => ({
        queueStats: state.queueStats.map((q) =>
          q.name === name ? { ...q, paused: true } : q
        ),
        isPausingQueue: null,
      }));
      return true;
    } catch (error) {
      set({
        isPausingQueue: null,
        queueError: {
          code: 'PAUSE_QUEUE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to pause queue',
        },
      });
      return false;
    }
  },

  resumeQueue: async (name: string): Promise<boolean> => {
    set({ isResumingQueue: name, queueError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.OPERATIONS.QUEUE_RESUME(name), {});

      // Update local state optimistically
      set((state) => ({
        queueStats: state.queueStats.map((q) =>
          q.name === name ? { ...q, paused: false } : q
        ),
        isResumingQueue: null,
      }));
      return true;
    } catch (error) {
      set({
        isResumingQueue: null,
        queueError: {
          code: 'RESUME_QUEUE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to resume queue',
        },
      });
      return false;
    }
  },

  // ============================================
  // Provider Actions
  // ============================================

  loadProviderStatus: async () => {
    set({ isLoadingProviders: true, providerError: null });
    try {
      const api = getApiClient();
      const data = await api.get<ProviderStatus[]>(API_ENDPOINTS.SUPER_ADMIN.OPERATIONS.PROVIDERS_STATUS);

      set({
        providerStatus: Array.isArray(data) ? data : [],
        isLoadingProviders: false,
      });
    } catch (error) {
      set({
        isLoadingProviders: false,
        providerError: {
          code: 'LOAD_PROVIDERS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load provider status',
        },
      });
    }
  },

  // ============================================
  // Cache Actions
  // ============================================

  invalidateCache: async (): Promise<boolean> => {
    set({ isClearingCache: true, cacheError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.OPERATIONS.CACHE_INVALIDATE, {});

      set({
        isClearingCache: false,
        lastCacheClearAt: new Date(),
      });
      return true;
    } catch (error) {
      set({
        isClearingCache: false,
        cacheError: {
          code: 'CLEAR_CACHE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to clear cache',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({
      healthError: null,
      maintenanceError: null,
      queueError: null,
      providerError: null,
      cacheError: null,
    });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get overall health status */
export const useOverallHealthStatus = (): HealthStatus => {
  return useOperationsStore((state) => state.healthStatus?.overallStatus ?? 'healthy');
};

/** Get unhealthy services (degraded or down) */
export const useUnhealthyServices = () => {
  return useOperationsStore((state) =>
    state.healthStatus?.services.filter(
      (s) => s.status === 'degraded' || s.status === 'down'
    ) ?? []
  );
};

/** Get paused queues */
export const usePausedQueues = () => {
  return useOperationsStore((state) =>
    state.queueStats.filter((q) => q.paused)
  );
};

/** Get failed queue jobs count */
export const useFailedJobsCount = () => {
  return useOperationsStore((state) =>
    state.queueStats.reduce((sum, q) => sum + q.failed, 0)
  );
};

/** Get unhealthy providers */
export const useUnhealthyProviders = () => {
  return useOperationsStore((state) =>
    state.providerStatus.filter(
      (p) => p.status === 'degraded' || p.status === 'down'
    )
  );
};

/** Check if any operations data is loading */
export const useOperationsLoading = () => {
  const isLoadingHealth = useOperationsStore((state) => state.isLoadingHealth);
  const isLoadingQueues = useOperationsStore((state) => state.isLoadingQueues);
  const isLoadingProviders = useOperationsStore((state) => state.isLoadingProviders);
  const isLoadingMaintenance = useOperationsStore((state) => state.isLoadingMaintenance);
  return isLoadingHealth || isLoadingQueues || isLoadingProviders || isLoadingMaintenance;
};

/** Check if maintenance mode is active */
export const useIsMaintenanceActive = () => {
  return useOperationsStore((state) => state.maintenanceMode?.isActive ?? false);
};
